import { computeAgentSignupCommission } from '@kilat/shared';
import type { AgentTier, CommissionRules } from '@kilat/shared';

/**
 * Pure, env-agnostic earnings math for the `/become-an-agent` calculator.
 *
 * The agent commission model is **one-time** (Sprint 95): an agent earns a
 * single commission the moment an operator they signed up pays their first
 * invoice. That commission equals one month of the operator's subscription
 * × `signupCommissionMultiplier` × the agent's tier multiplier — paid ONCE
 * per operator, never repeating.
 *
 * This helper does NOT invent its own per-signup math: it delegates to the
 * shared `computeAgentSignupCommission` (the exact function the
 * `commissionAccrue` trigger runs in production), feeding it a synthetic
 * single-line `subscription` invoice for the chosen monthly amount. That
 * guarantees the marketing estimate matches what the platform actually pays,
 * floor-rounding and all.
 *
 * On top of that, it walks the auto-promotion tier ladder: an agent starts at
 * Bronze (×1.0), is promoted to Silver (×1.1) once they reach
 * `silverActiveOperators` signups, and to Gold (×1.25) at
 * `goldActiveOperators`. Each signup is paid at the tier the agent occupies
 * when it lands, so the total is the sum of per-tier subtotals rather than a
 * single flat multiple.
 */

export interface AgentEarningsTierRow {
  readonly tier: AgentTier;
  /** How many of the visitor's signups are paid at this tier. */
  readonly count: number;
  /** One-time commission earned for a single signup at this tier, in sen. */
  readonly perSignupSen: number;
  /** `count × perSignupSen`, in sen. */
  readonly subtotalSen: number;
}

export interface AgentEarnings {
  /** Total one-time commission across every signup, in integer sen. */
  readonly totalSen: number;
  /** Bronze → Silver → Gold, always three rows (a tier may have count 0). */
  readonly tierBreakdown: readonly AgentEarningsTierRow[];
}

const TIER_ORDER: readonly AgentTier[] = ['bronze', 'silver', 'gold'];

/** Coerce a possibly-empty / NaN numeric input to a non-negative integer. */
function toNonNegInt(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

/**
 * One-time commission for a single signup at a given tier, in sen. Mirrors
 * production exactly by routing through the shared accrual helper with a
 * synthetic one-line subscription invoice.
 */
function perSignupSenFor(monthlySubscriptionSen: number, rules: CommissionRules, tier: AgentTier) {
  return computeAgentSignupCommission({
    lineItems: [{ kind: 'subscription', amount: monthlySubscriptionSen }],
    rules,
    tier,
  }).totalSen;
}

export function computeAgentEarnings(input: {
  readonly operatorCount: number;
  readonly monthlySubscriptionSen: number;
  readonly rules: CommissionRules;
}): AgentEarnings {
  const { rules } = input;
  const operatorCount = toNonNegInt(input.operatorCount);
  const monthlySubscriptionSen = toNonNegInt(input.monthlySubscriptionSen);

  const silverThreshold = rules.autoPromotionThresholds.silverActiveOperators;
  const goldThreshold = rules.autoPromotionThresholds.goldActiveOperators;

  // Signups are paid at the tier the agent occupies as each one lands:
  //   Bronze  for signups 1..silverThreshold
  //   Silver  for signups (silverThreshold + 1)..goldThreshold
  //   Gold    for signups (goldThreshold + 1)..
  const silverSlots = Math.max(goldThreshold - silverThreshold, 0);
  const counts: Record<AgentTier, number> = {
    bronze: Math.min(operatorCount, silverThreshold),
    silver: Math.min(Math.max(operatorCount - silverThreshold, 0), silverSlots),
    gold: Math.max(operatorCount - goldThreshold, 0),
  };

  let totalSen = 0;
  const tierBreakdown = TIER_ORDER.map<AgentEarningsTierRow>((tier) => {
    const count = counts[tier];
    const perSignupSen = perSignupSenFor(monthlySubscriptionSen, rules, tier);
    const subtotalSen = count * perSignupSen;
    totalSen += subtotalSen;
    return { tier, count, perSignupSen, subtotalSen };
  });

  return { totalSen, tierBreakdown };
}
